<script setup lang="ts">
import type { AssigneeItem, DetailedAssignee } from '@/types/assignee';
import type { Executor } from '@/types/executor';
import type { NameIdPair } from '@/types/nameIdPair';
import type { Responsible } from '@/types/responsible';
import { computed, defineModel, defineProps, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
    employees: NameIdPair[];
    teams: NameIdPair[];
    entityTypeLabel: string;
    autocompleteLabel: string;
}>();

const { t: translate } = useI18n();

const possibleTypes: AssigneeItem[] = [
    { title: translate('enums.EntityType.Employee'), value: 'Employee' },
    { title: translate('enums.EntityType.Team'), value: 'Team' }
];

const type = defineModel<'Employee' | 'Team'>('type');

const initialChosenEntity = defineModel<DetailedAssignee | null | undefined>('initialChosenEntity');

const valueDiffersFromInitial = computed(() => initialChosenEntity.value && initialChosenEntity.value._id !== modelValue.value?._id);

const modelValue = defineModel<DetailedAssignee | null | undefined>();

function getItemValue(item: NameIdPair): Responsible | Executor {
    return {
        entityId: item._id,
        entityType: type.value as 'Employee' | 'Team'
    };
}

function onTypeChange() {
    modelValue.value = undefined;
}

function resetToInitial() {
    if (type.value != initialChosenEntity.value?.type) type.value = initialChosenEntity.value?.type;
    modelValue.value = initialChosenEntity.value ?? undefined;
}

onMounted(() => {
    initialChosenEntity.value = modelValue.value;
});
</script>

<template>
    <v-row dense class="align-center">
        <v-col :cols="type ? 5 : 12">
            <v-select
                v-model="type"
                :label="entityTypeLabel"
                :items="possibleTypes"
                item-title="title"
                item-value="value"
                @update:model-value="onTypeChange"
                clearable
            />
        </v-col>
        <v-col cols="1" class="d-flex justify-center" v-if="type">
            <v-icon icon="arrow_right" size="30" class="text-primary" />
        </v-col>
        <v-col cols="6" v-if="type" class="d-flex align-center ga-2">
            <v-autocomplete
                v-model="modelValue"
                :label="autocompleteLabel"
                :items="type === 'Employee' ? props.employees : props.teams"
                item-title="name"
                :item-value="getItemValue"
                clearable
            />
            <v-col cols="1" v-if="valueDiffersFromInitial">
                <v-tooltip text="Herstel naar initiale waarde">
                    <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" icon="refresh" @click="resetToInitial" class="text-primary-dark" />
                    </template>
                </v-tooltip>
            </v-col>
        </v-col>
    </v-row>
</template>

<style scoped></style>
